import { defineMessages, injectIntl } from 'react-intl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import dataComponent from '../hoc/dataComponent';
import shareableEntity from '../hoc/shareableEntity';
import { ENTITY_TYPE_ARTISTS, META_TITLE_PREFIX } from '../constants';
import * as clientActions from '../actions/client';
import * as localListSearchActions from '../actions/localListSearch';
import { selectSearchQuery } from '../selectors/localListSearch';

const metaTitles = defineMessages({
  composers: {
    id: 'composers.meta.title',
    defaultMessage: 'Composers',
  },
  conductors: {
    id: 'conductors.meta.title',
    defaultMessage: 'Conductors',
  },
  soloists: {
    id: 'soloists.meta.title',
    defaultMessage: 'Soloists',
  },
  ensembles: {
    id: 'ensembles.meta.title',
    defaultMessage: 'Ensembles',
  },
});

const metaDescriptions = defineMessages({
  composers: {
    id: 'composers.meta.description',
    defaultMessage:
      'Find and play curated classical music from hundreds of composers. Compare alternative recordings and browse by performer.',
  },
  conductors: {
    id: 'conductors.meta.description',
    defaultMessage:
      'Find and play curated classical music from hundreds of conductors. Compare alternative recordings and browse by conductor.',
  },
  soloists: {
    id: 'soloists.meta.description',
    defaultMessage:
      'Find and play curated classical music from hundreds of soloists. Compare alternative recordings and browse by soloist.',
  },
  ensembles: {
    id: 'ensembles.meta.description',
    defaultMessage:
      'Find and play classical music recordings by hundreds of ensembles. Compare alternative recordings and browse by composer.',
  },
});

const getArtistListMapStateToProps = (
  artistType,
  selectItems,
  selectIsLoading,
  selectNextCursor,
  selectPrevCursor
) => {
  return function mapStateToProps(state, ownProps) {
    const searchQuery = selectSearchQuery(state, artistType);
    const compoundId = 'query-' + searchQuery;

    return {
      items: selectItems(state, compoundId),
      currentCursor: ownProps.params.cursor,
      nextCursor: selectNextCursor(state, compoundId),
      prevCursor: selectPrevCursor(state, compoundId),
      isLoading: selectIsLoading(state, compoundId),
      searchQuery,
      artistType,
      title: metaTitles[artistType],
    };
  };
};

const getArtistListFetchData = (loadAction, searchKey) => {
  return function fetchData(store, { cursor }) {
    return [
      store.dispatch(loadAction(cursor)),
      store.dispatch(localListSearchActions.setSearchQuery(searchKey, '')),
    ];
  };
};

const getArtistListDispatchProps = (loadAction, searchKey) => ({
  loadItems: loadAction,
  setSearchQuery: query => localListSearchActions.setSearchQuery(searchKey, query),
});

const getShareTrackingContext = artistType => {
  return {
    sharedContentType: artistType === 'ensembles' ? 'Ensemble' : 'Artist',
    sharedContentId: null,
    contextType: 'Category',
    contentId: null,
  };
};

const getArtistListOnDataFetch = (artistType, selectNextCursor, selectPrevCursor) => {
  return (store, params, location, intl) => {
    const state = store.getState();
    const metaTitle = metaTitles[artistType];
    const metaDescription = metaDescriptions[artistType];
    store.dispatch(
      clientActions.setHelmet({
        title: META_TITLE_PREFIX + intl.formatMessage(metaTitle),
        description: intl.formatMessage(metaDescription),
        canonicalRoute: `/${artistType}`,
        currentCursor: params.cursor,
        nextCursor: selectNextCursor(state),
        prevCursor: selectPrevCursor(state),
      })
    );
  };
};

export default function createArtistListRoute({
  artistType,
  selectItems,
  selectIsLoading,
  selectNextCursor,
  selectPrevCursor,
  loadAction,
}) {
  const fetchData = getArtistListFetchData(loadAction, artistType);
  const mapStateToProps = getArtistListMapStateToProps(
    artistType,
    selectItems,
    selectIsLoading,
    selectNextCursor,
    selectPrevCursor
  );
  const dispatchProps = getArtistListDispatchProps(loadAction, artistType);
  const getShareTrackingContextFn = () => getShareTrackingContext(artistType);
  const onDataFetch = getArtistListOnDataFetch(artistType, selectNextCursor, selectPrevCursor);

  const composedHOC = compose(
    dataComponent(fetchData),
    connect(mapStateToProps, dispatchProps),
    injectIntl,
    shareableEntity(ENTITY_TYPE_ARTISTS, getShareTrackingContextFn)
  );

  composedHOC.onDataFetch = onDataFetch;

  return composedHOC;
}
